import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Empty, PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { RetiredAccounts } from "@/components/admin/RetiredAccounts";

const Row = ({ u, onDecide, onChange, busy }) => (
  <div className="surface flex flex-wrap items-center gap-3 p-4" data-testid={`access-row-${u.user_id}`}>
    {u.avatar ? <img src={u.avatar} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="h-10 w-10 rounded-full bg-slate-200" />}
    <div className="min-w-0 flex-1">
      <p className="font-medium">{u.name}</p>
      <p className="truncate text-sm text-slate-500">{u.email} · joined {timeAgo(u.created_at)}{u.paused_at && ` · paused ${timeAgo(u.paused_at)}`}</p>
      {u.approval === "paused" && u.pause_reason && <p className="text-xs text-rose-700" data-testid={`access-reason-${u.user_id}`}>{u.pause_reason}</p>}
    </div>
    {onDecide ? (
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full" disabled={busy} onClick={() => onDecide(u, "approve")} data-testid={`access-approve-${u.user_id}`}>Restore</Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onDecide(u, "decline")} data-testid={`access-decline-${u.user_id}`}>Decline</Button>
      </div>
    ) : <div className="flex items-center gap-2"><span className={`text-xs font-semibold uppercase ${u.approval === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{u.approval} {timeAgo(u.approval_at)}</span>
      {onChange && <Button size="sm" variant="ghost" className="h-7 rounded-full px-3 text-xs" onClick={() => onChange(u, u.approval === "approved" ? "decline" : "approve")} data-testid={`access-change-${u.user_id}`}>{u.approval === "approved" ? "Decline" : "Restore"}</Button>}</div>}
  </div>
);

export default function AdminAccess() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/access").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const decide = async (u, action) => {
    setBusy(u.user_id);
    try { await api.post(`/admin/access/${u.user_id}`, { action }); toast.success(action === "approve" ? `${u.name} restored` : `${u.name} declined`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  if (!d) return <Spinner />;
  return (
    <div className="space-y-8" data-testid="admin-access-page">
      <PageHeader env="admin" title="People & bot checks" subtitle="Anyone can join. Sign-up has a built-in bot check and needs a confirmed email. New members have lower daily limits for a week, their first posts get an AI check, and accounts that look automated are paused here for you to review. Restored people skip the automatic checks." />
      <section className="space-y-3">
        <h2 className="text-2xl">Paused for review ({d.paused.length})</h2>
        {d.paused.length ? d.paused.map((u) => <Row key={u.user_id} u={u} onDecide={decide} busy={busy === u.user_id} />) : <Empty testid="access-empty" title="No paused accounts" text="Accounts that look automated appear here and in your notifications." />}
      </section>
      {d.recent.length > 0 && <section className="space-y-3"><h2 className="text-2xl">Recent decisions</h2>{d.recent.map((u) => <Row key={u.user_id} u={u} onChange={decide} />)}</section>}
      <RetiredAccounts />
    </div>
  );
}
